/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import type { ReactNode } from 'react';
import { Field, Input as ChakraInput, type InputProps as ChakraInputProps } from 'chakra-v3';

export interface InputProps extends Omit<ChakraInputProps, 'onChange'> {
  /**
   * Placeholder text for the input
   */
  placeholder?: string;
  /**
   * Current value of the input
   */
  value?: string;
  /**
   * Callback function called when the input value changes
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Optional field label rendered above the input
   */
  label?: ReactNode;
  /**
   * Optional color for the label text
   */
  labelColor?: string;
  /**
   * Helper text displayed below the input
   */
  helperText?: ReactNode;
  /**
   * Error text displayed when the field is invalid
   */
  errorText?: ReactNode;
  /**
   * Marks the field as required
   */
  isRequired?: boolean;
  /**
   * Marks the field as invalid
   */
  isInvalid?: boolean;
}

/**
 * Reusable Input Component
 *
 * A styled input component that matches the design system.
 * Uses dark teal background with light blue border, matching the login form design.
 *
 * @example
 * ```tsx
 * <Input
 *   placeholder="Enter your phone number"
 *   value={phoneNumber}
 *   onChange={(e) => setPhoneNumber(e.target.value)}
 *   type="tel"
 * />
 * ```
 */
export const Input: React.FC<InputProps> = ({
  placeholder,
  value,
  onChange,
  label,
  labelColor,
  helperText,
  errorText,
  isInvalid,
  isRequired,
  disabled,
  ...rest
}) => {
  const inputElement = (
    <ChakraInput
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      bg="brand.tealDark"
      borderColor={isInvalid ? 'feedback.error' : 'cta.solid'}
      color="brand.grey20"
      _placeholder={{
        color: 'brand.grey20',
      }}
      _focus={{
        borderColor: 'brand.secondaryBlue2',
        outline: 'none',
      }}
      _disabled={{
        opacity: 0.6,
        cursor: 'not-allowed',
      }}
      h="48px"
      px="16px"
      py="12px"
      borderRadius="4px"
      fontSize="16px"
      fontFamily="body"
      lineHeight="1.4"
      letterSpacing="0.08px"
      w="100%"
      aria-invalid={isInvalid ? true : undefined}
      disabled={disabled}
      {...rest}
    />
  );

  if (!label && !helperText && !errorText) {
    return inputElement;
  }

  return (
    <Field.Root w="100%" required={isRequired} invalid={isInvalid}>
      {label ? <Field.Label color={labelColor}>{label}</Field.Label> : null}
      {inputElement}
      {helperText ? <Field.HelperText>{helperText}</Field.HelperText> : null}
      {isInvalid && errorText ? <Field.ErrorText>{errorText}</Field.ErrorText> : null}
    </Field.Root>
  );
};
